import React from "react";
import {
  Box,
  Button,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Switch,
  Tabs,
  TabsList,
  TabsTab,
  Text,
} from "@salestrak/ui";

import type { MapSettings } from "./map-styles";
import type { Omittable } from "@salestrak/ui";

type SettingRowProps = Omittable<{
  label: string;
  subtitle?: string;
  checked: boolean;
  onCheckedChange: (val: boolean) => void;
  disabled?: boolean;
}>;

function SettingRow({
  label,
  subtitle,
  checked,
  onCheckedChange,
  disabled,
}: SettingRowProps) {
  return (
    <Box align="start" direction="row" gap="xl" paddingY="xs">
      <Box flex={1} gap="xs">
        <Text size="sm" weight="medium">
          {label}
        </Text>
        {subtitle ? (
          <Text size="xs" variant="muted">
            {subtitle}
          </Text>
        ) : null}
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </Box>
  );
}

type MapStylePopoverProps = {
  settings: MapSettings;
  onSettingsChange: (settings: MapSettings) => void;
};

export function MapStylePopover({
  settings,
  onSettingsChange,
}: MapStylePopoverProps) {
  const update = (patch: Partial<MapSettings>) =>
    onSettingsChange({ ...settings, ...patch });

  const isHybrid = settings.mapTypeId === "hybrid";

  return (
    <Popover>
      {/* Icon paints with `currentColor`, so the muted token tints the gear —
        * not the map palette's resolved `controlIcon` hex. */}
      <PopoverTrigger
        render={
          <Button aria-label="Map settings" size="icon-xs" variant="ghost">
            <Icon color="muted" name="settings" size="md" />
          </Button>
        }
      />

      <PopoverPopup align="end" className="w-56" sideOffset={8}>
        <Box gap="xs">
          <Tabs
            onValueChange={(val) =>
              update({ mapTypeId: val as MapSettings["mapTypeId"] })
            }
            value={settings.mapTypeId}
          >
            <TabsList className="w-full">
              <TabsTab value="terrain">Terrain</TabsTab>
              <TabsTab value="hybrid">Satellite</TabsTab>
            </TabsList>
          </Tabs>

          <SettingRow
            checked={isHybrid || settings.roadNames}
            disabled={isHybrid}
            label="Road names"
            onCheckedChange={(val) => update({ roadNames: val })}
          />
          <SettingRow
            checked={isHybrid || settings.pointsOfInterest}
            disabled={isHybrid}
            label="Points of interest"
            onCheckedChange={(val) => update({ pointsOfInterest: val })}
          />
          <SettingRow
            checked={isHybrid || settings.transitLines}
            disabled={isHybrid}
            label="Transit lines"
            onCheckedChange={(val) => update({ transitLines: val })}
          />
          <SettingRow
            checked={settings.accuracySensors}
            label="Higher accuracy"
            onCheckedChange={(val) => update({ accuracySensors: val })}
            subtitle={"Uses device sensors. Increases battery usage."}
          />
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
